/**
 * Login Page
 * Paper Mode - Editorial Systems Aesthetic
 *
 * Flow:
 * 1. User clicks "Sign in" button
 * 2. Redirects to /api/v1/auth/login (our backend)
 * 3. Backend redirects to auth provider
 * 4. User authenticates on hosted UI
 * 5. Callback to /api/v1/auth/callback (our backend)
 * 6. Backend creates session cookie and redirects to /
 */

import { useEffect, useState } from 'react';
import { authService } from '@/services/auth';
import { useAuthStore } from '@/store/authStore';
import styles from './LoginPage.module.css';

export function LoginPage() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const sessionChecked = useAuthStore((state) => state.sessionChecked);
  const error = useAuthStore((state) => state.error);
  const [isRedirecting, setIsRedirecting] = useState(false);

  useEffect(() => {
    // If already authenticated, redirect to app
    if (sessionChecked && isAuthenticated) {
      window.location.href = '/';
    }
  }, [sessionChecked, isAuthenticated]);

  const handleLogin = () => {
    setIsRedirecting(true);
    // Redirect immediately; "Redirecting..." shows on click (Phase 1: remove artificial delay)
    authService.redirectToLogin();
  };

  // Show loading while checking session
  if (!sessionChecked) {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <div className={styles.loading}>
            <div className={styles.loadingSpinner} />
            <p className={styles.loadingText}>Checking session...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        {/* Status badge */}
        <div className={styles.statusBadge}>
          <span className={styles.statusDot} />
          <span>SECURE LOGIN</span>
        </div>

        {/* Logo - matches landing page */}
        <div className={styles.logoContainer}>
          <div className={styles.logo}>
            <img src="/logo-icon.svg" alt="Semaphoric" className={styles.logoImg} />
          </div>
        </div>

        <h1 className={styles.title}>Welcome to Semaphoric</h1>
        <p className={styles.subtitle}>
          Sign in to access your network intelligence platform
        </p>

        {/* Error message */}
        {error && (
          <div className={styles.error}>
            {error}
          </div>
        )}

        {/* Sign in button */}
        <button
          onClick={handleLogin}
          disabled={isRedirecting}
          className={styles.signInButton}
        >
          {isRedirecting ? (
            <>
              <div className={styles.spinner} />
              Redirecting...
            </>
          ) : (
            <>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
                <polyline points="10 17 15 12 10 7" />
                <line x1="15" y1="12" x2="3" y2="12" />
              </svg>
              Sign in
            </>
          )}
        </button>
      </div>
    </div>
  );
}

export default LoginPage;
